import type { GTooltipCardData } from '~/presentation/ui';

import { ICampaignMetricKey } from '../types/chart-tooltip';
import { EAnalyticMode } from '~/modules/analytics/types';

type ICampaignMetricKeyValue = Record<ICampaignMetricKey, GTooltipCardData>;
type ICampaignMetricKeyValueByMode = Record<EAnalyticMode, ICampaignMetricKeyValue>;

const CAMPAIGN_METRIC_TOOLTIP: ICampaignMetricKeyValue = {
  [ICampaignMetricKey.SESSION]: {
    title: 'Sessions',
    content: 'A period during which a visitor interacts with your online store',
  },
  [ICampaignMetricKey.BOUNCE_RATE]: {
    title: 'Bounce rate',
    content:
      'Percentage of visitors who leave your store after viewing only one page, without interacting further.',
    formula: (
      <>
        <span className="formula-variable">Bounce rate = </span>
        <span className="formula-input">sessions with a pageview / total sessions</span>
      </>
    ),
  },
  [ICampaignMetricKey.CTR]: {
    title: 'Click-through rate',
    content:
      'Percentage of clicks to open new page, compared to the number of times the page was viewed',
    formula: (
      <>
        <span className="formula-variable">Click-through rate = </span>
        <span className="formula-input">total clicks / total pageviews</span>
      </>
    ),
  },
  [ICampaignMetricKey.AVG_TIME]: {
    title: 'Average time on page',
    content: 'Average duration that visitors spend on a page before clicking to open new page',
    formula: (
      <>
        <span className="formula-variable">Average time on page = </span>
        <span className="formula-input">total time on page / (total pageviews - total exits)</span>
      </>
    ),
  },
  [ICampaignMetricKey.PAGE_VIEWED]: {
    title: 'Pageviews',
    content: 'Number of times a page on your online store has been viewed by visitors',
  },
  [ICampaignMetricKey.RPV]: {
    title: 'Revenue per visitor',
    content: 'Average revenue generated from each visitor',
    formula: (
      <>
        <span className="formula-variable">Revenue per visitor = </span>
        <span className="formula-input">total revenue / total visitors</span>
      </>
    ),
  },
  [ICampaignMetricKey.REVENUE]: {
    title: 'Revenue',
    content: 'Total income generated from sales after discounts',
    formula: (
      <>
        <span className="formula-variable">Revenue = </span>
        <span className="formula-input">gross sales - discounts</span>
      </>
    ),
  },
  [ICampaignMetricKey.VISITOR_BY_TYPE]: {
    title: 'Visitors by type',
    content: 'Numbers of new and returning visitors',
    contentList: [
      'New visitor: who accesses your store for the first time after GemX installation',
      'Returning visitor: who comes back to your store after GemX installation',
    ],
  },
  [ICampaignMetricKey.PATH_ANALYSIS]: {
    title: 'Journey analysis',
    content:
      'Journey analysis tracks the journey from entry to purchase and shows where visitors leave the store',
  },
  [ICampaignMetricKey.ADD_TO_CART]: {
    title: 'Add to cart',
    content:
      'Total number of the event when a customer adds a product to their cart on your online store',
  },
  [ICampaignMetricKey.ADD_TO_CART_RATE]: {
    title: 'Add to cart rate',
    content: 'Percentage of sessions in which the customer added item to the cart',
    formula: (
      <>
        <span className="formula-variable">Added to cart rate = </span>
        <span className="formula-input">sessions with cart additions / total sessions</span>
      </>
    ),
  },
  [ICampaignMetricKey.ORDER]: {
    title: 'Orders',
    content: 'Number of orders that went through tested pages in this experiment',
  },
  //
  [ICampaignMetricKey.CONVERSION_RATE]: {
    title: 'Conversion rate',
    content: 'Percentage of online store sessions that completed an order',
    formula: (
      <>
        <span className="formula-variable">Conversion rate = </span>
        <span className="formula-input">sessions that completed checkout / total sessions</span>
      </>
    ),
  },
  [ICampaignMetricKey.AOV]: {
    title: 'Average order value',
    content: 'Average value of orders placed in your online store',
    formula: (
      <>
        <span className="formula-variable">Average order value = </span>
        <span className="formula-input">total revenue / total orders</span>
      </>
    ),
  },
  [ICampaignMetricKey.VISITOR]: {
    title: 'Visitors',
    content: 'Number of unique individuals who visit your online store',
  },

  // Session
  [ICampaignMetricKey.SESSION_REACHED_CHECKOUT]: {
    title: 'Sessions that reached checkout',
    content: 'Sessions in which the checkout page was reached',
  },
  [ICampaignMetricKey.SESSION_THAT_REACHED_CHECKOUT]: {
    title: 'Sessions that reached checkout',
    content: 'Sessions in your online store in which the checkout page was reached',
  },
  [ICampaignMetricKey.SESSION_THAT_COMPLETED_CHECKOUT]: {
    title: 'Sessions that completed checkout',
    content: 'Sessions in your online store in which a purchase was completed',
  },
  [ICampaignMetricKey.SESSION_WITH_CART_ADDITION]: {
    title: 'Sessions with cart additions',
    content: 'Sessions in your online store in which a visitor added item to the cart',
  },
  [ICampaignMetricKey.SESSIONS_BY_DEVICE_TYPE]: {
    title: 'Sessions by device type',
    content: "Sessions in your online store by the visitor's device type",
  },
  [ICampaignMetricKey.SESSIONS_BY_TRAFFIC_SOURCE]: {
    title: 'Sessions by traffic source',
    content: 'Sessions on your page by where visitors come from',
  },
};

export const CAMPAIGN_METRIC_TOOLTIP_BY_MODE: ICampaignMetricKeyValueByMode = {
  [EAnalyticMode.FIRST_SESSION]: {
    ...CAMPAIGN_METRIC_TOOLTIP,
  },
  [EAnalyticMode.ALL_SESSION]: {
    ...CAMPAIGN_METRIC_TOOLTIP,
  },
  [EAnalyticMode.PAGE_ONLY]: {
    ...CAMPAIGN_METRIC_TOOLTIP,
  },
};
